import { useToast } from 'components/ToastManager';
import { useUpdateUnitLinkMutation } from 'hooks/internalApi';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { useCatchMutationError } from 'utils/urql';

import { decodeBankLinkId } from '@fragment/shared-utils/ids/bankAccount';
import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Icon } from '@fragment/ui/src/components/Icon';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

type FormData = {
  orgToken?: string;
};

type Props = {
  workspaceId: string;
  encodedLinkId: string;
  onFormClose: VoidFunction;
};

export const UpdateUnitLinkForm = ({
  workspaceId,
  encodedLinkId,
  onFormClose,
}: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      orgToken: '',
    },
  });
  const { linkId } = decodeBankLinkId(encodedLinkId);

  const { showToast } = useToast();
  const { context } = useInternalApiContext();
  const [
    { error: updateUnitLinkError, data: updateUnitLinkData },
    updateUnitLink,
  ] = useCatchMutationError(useUpdateUnitLinkMutation, 'updateUnitLink');

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (data.orgToken) {
        const updateUnitLinkResp = await updateUnitLink(
          {
            update: {
              workspaceId,
              linkId,
              orgToken: data.orgToken,
              // Comment the beneath line out if you want to test unit syncing in dev.
              // This is disabled so we don't end up throttling the syncTxs lambda
              // since dev is a shared AWS env
              disableSync: process.env.NODE_ENV === 'development',
            },
          },
          context
        );
        if (
          updateUnitLinkResp.data?.updateUnitLink.__typename ===
          'UpdateUnitLinkResult'
        ) {
          showToast(<Toast type="success" message="Updated Unit Token." />);
          onFormClose();
        } else {
          showToast(
            <Toast
              type="error"
              message={`Error: (${updateUnitLinkResp.data?.updateUnitLink.code}) ${updateUnitLinkResp.data?.updateUnitLink.message}`}
            />
          );
        }
      }
      reset(data);
    },
    [
      linkId,
      reset,
      updateUnitLink,
      workspaceId,
      context,
      onFormClose,
      showToast,
    ]
  );

  const hasError = !!updateUnitLinkError || !!errors.orgToken;
  const onCancel = useCallback(() => {
    onFormClose();
    reset();
  }, [reset, onFormClose]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
    <form className="w-full space-y-f2" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <TextInput
          className="color-negative"
          label="Org API Token"
          flexContainer="space-y-0"
          placeholder="New API Token"
          width="w-1/2"
          padding="p-f0"
          size="inherit"
          hasError={hasError}
          id="orgToken"
          {...register('orgToken', {
            required: false,
          })}
        />
        <div className="text-main-500">This token will not be shown again.</div>
      </div>
      <div>
        <FormButtons
          type="tertiary"
          onCancel={onCancel}
          cancelButtonLabel="Cancel"
          submitButtonLabel={isSubmitting ? 'Saving...' : 'Save Changes'}
          disabled={isSubmitting || !isValid || !isDirty}
        />
      </div>
      {(updateUnitLinkData || hasError) && (
        <div className="pt-f2">
          {updateUnitLinkData?.updateUnitLink.__typename ===
            'UpdateUnitLinkResult' && (
            <div className="flex flex-row items-center">
              <Icon type="success" backgroundColor="black" />
              &nbsp;Link updated
            </div>
          )}
          {hasError &&
            match([updateUnitLinkError, errors.orgToken])
              .with([P.not(undefined), P._], () => (
                <InlineError>Failed to update the link</InlineError>
              ))
              .with([P._, P.not(undefined)], ([_, formFieldErrors]) => (
                <InlineError>
                  {formFieldErrors.message ?? 'Form has errors'}
                </InlineError>
              ))
              .otherwise(() => null)}
        </div>
      )}
    </form>
  );
};
